import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import GroupeInfo from "./DiscussionComponents/GroupeInfo";
import ChatForm from "./ChatFormWithAudio";
import ReplyPreview from "./chat/ReplyPreview";
import { listOptionsDiscussion } from "./DiscussionComponents/list-options-discussion";
import { DisplayDiscussionForMiniScreenContext } from "../contexts/DisplayDiscussionForMiniScreenContext";
import { AuthContext } from "../contexts/AuthContext";
import { WorkspaceContext } from "../contexts/WorkspaceContext";
import { socket } from "../services/socket";
import MyMessage from "./chat/MyMessage";
import YourMessage from "./chat/YourMessage";
import { formatUnixToDate, formatMessageDate } from "../services/getTimeSended";
import { ThemeContext } from "../contexts/ThemeContext";
import OffCanvasRightGroupe from "./OffCanvasRightGroupe";
import MediaGalleryModal from "./chat/MediaGalleryModal";
import {
  normalizeFileContenu,
  isImageFormat,
} from "../services/normalizeContenu";
import { includesId, idsEqual } from "../services/idUtils";
import {
  getCachedMessages,
  cacheMessages,
  cacheSingleMessage,
  removeCachedMessage,
} from "../services/messageCache";
import Person from "./Person";

export default function DiscussionGroupe() {
  const { theme } = useContext(ThemeContext);
  const [bg, setBg] = useState(theme === "light" ? "#0156cd" : "#f8f8f8");
  const [color, setColor] = useState(theme === "light" ? "#f8f8f8" : "#0156cd");
  const [allMessages, setAllMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const {
    displayDiscussionForMiniScreen,
    crew,
    setCrew,
    activeRoomIdGroupe,
    activeRawRoomIdGroupe,
    typingGroupeMap,
    clearTypingGroupe,
  } = useContext(DisplayDiscussionForMiniScreenContext);
  const [displayOffCanvasRight, setDisplayOffCanvasRight] = useState(false);
  const { user } = useContext(AuthContext);
  const { currentWorkspace } = useContext(WorkspaceContext);
  const workspaceId = currentWorkspace?._id;
  const [show, setShow] = useState(false);
  const [stickyDate, setStickyDate] = useState(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const validatedRoomsRef = useRef(new Set());
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const typingUsers = activeRawRoomIdGroupe ? typingGroupeMap[activeRawRoomIdGroupe] || [] : [];

  const VIDEO_FORMATS = ["mp4", "mov", "avi", "webm"];
  const allMedia = useMemo(() => {
    const media = [];
    allMessages.forEach((msg) => {
      if (msg.nature === "fichier") {
        const files = normalizeFileContenu(msg.contenu);
        files.forEach((file) => {
          const fmt = file.format?.toLowerCase();
          if (isImageFormat(fmt) || VIDEO_FORMATS.includes(fmt)) {
            media.push({ url: file.fileUrl, format: fmt, nom: file.nom });
          }
        });
      }
    });
    return media;
  }, [allMessages]);

  const handleOpenGallery = useCallback(
    (fileUrl) => {
      const idx = allMedia.findIndex((m) => m.url === fileUrl);
      setGalleryIndex(idx >= 0 ? idx : 0);
      setGalleryOpen(true);
    },
    [allMedia]
  );

  const handleDeleteMessage = useCallback((messageId) => {
    setAllMessages((prev) => prev.filter((msg) => msg.messageId !== messageId));
    removeCachedMessage(messageId);
  }, []);

  const handleSwipeReply = useCallback((msgObject) => {
    let displayName = "L'utilisateur";
    if (msgObject.emetteur === user.userId) {
      displayName = `${user.prenom} ${user.nom}`;
    } else if (crew?.membres) {
      const membre = crew.membres.find((m) => idsEqual(m.userId, msgObject.emetteur));
      if (membre) {
        displayName = `${membre.prenom} ${membre.nom}`;
      }
    }

    setReplyTo({
      replyToId: msgObject.messageId,
      replyToContenu: typeof msgObject.contenu === "string"
        ? msgObject.contenu
        : Array.isArray(msgObject.contenu)
          ? msgObject.contenu.map((f) => f.nom || f).join(", ")
          : "",
      replyToSender: {
        userId: msgObject.emetteur,
        displayName,
        userImage: msgObject.emetteurImage || "",
        colors: null,
      },
      replyToNature: msgObject.nature,
    });
  }, [user, crew]);

  const handleAfterSuppression = useCallback(
    (msgs) => {
      const messagesList = msgs.filter(
        (msg) => !includesId(msg.deletedBy, user.userId)
      );
      setAllMessages(messagesList);
      if (activeRawRoomIdGroupe) {
        cacheMessages(activeRawRoomIdGroupe, messagesList);
      }
    },
    [user.userId, activeRawRoomIdGroupe]
  );

  useEffect(() => {
    socket.on("receiveMsgGroupe", (msg) => {
      if (crew && msg.canal === activeRawRoomIdGroupe) {
        clearTypingGroupe(activeRawRoomIdGroupe, msg.emetteur);
        setAllMessages((prev) => [msg, ...prev]);
        cacheSingleMessage(msg);
      }
    });
    socket.on(
      "nouveau_message_groupe_au_compteur_pour_la_discussion",
      (message) => {
        if (crew && message.canal === activeRawRoomIdGroupe) {
          socket.emit(
            "je_declare_tous_les_messages_groupe_de_cette_discussion_ouvert",
            {
              destinataire: user.userId,
              roomId: message.canal,
              workspaceId,
            }
          );
        }
      }
    );
    socket.on("messages_precedents_groupe", (data) => {
      const raw = Array.isArray(data) ? data : data.messages;
      const more = Array.isArray(data) ? false : data.hasMore;
      const messagesList = raw.filter(
        (msg) => !includesId(msg.deletedBy, user.userId)
      );
      setAllMessages(messagesList);
      setHasMore(more);
      if (activeRawRoomIdGroupe) {
        validatedRoomsRef.current.add(activeRawRoomIdGroupe);
        cacheMessages(activeRawRoomIdGroupe, messagesList);
      }
    });
    socket.on("messages_plus_anciens_groupe", (data) => {
      const { messages, hasMore: more } = data;
      const messagesList = messages.filter(
        (msg) => !includesId(msg.deletedBy, user.userId)
      );
      setAllMessages((prev) => [...prev, ...messagesList]);
      setHasMore(more);
      setLoadingMore(false);
    });
    socket.on("messages_after_supression", handleAfterSuppression);
    socket.on("membre_muted_in_groupe", ({ roomId, groupe: g }) => {
      if (crew && activeRawRoomIdGroupe === roomId) {
        setCrew((prev) => ({ ...prev, mutedMembers: g.mutedMembers || [] }));
      }
    });
    return () => {
      socket.off("nouveau_message_groupe_au_compteur_pour_la_discussion");
      socket.off("receiveMsgGroupe");
      socket.off("messages_precedents_groupe");
      socket.off("messages_plus_anciens_groupe");
      socket.off("messages_after_supression", handleAfterSuppression);
      socket.off("membre_muted_in_groupe");
    };
  }, [socket, crew, activeRoomIdGroupe, activeRawRoomIdGroupe, handleAfterSuppression, workspaceId]);

  useEffect(() => {
    if (theme === "light") {
      setBg("#0156cd");
      setColor("#f8f8f8");
    } else {
      setBg("#f8f8f8");
      setColor("#0156cd");
    }
  }, [theme]);

  // Ouvrir l'off-canvas droit depuis OptionsList
  useEffect(() => {
    const handleOpenDetails = () => setDisplayOffCanvasRight(true);
    window.addEventListener("open_groupe_details", handleOpenDetails);
    return () =>
      window.removeEventListener("open_groupe_details", handleOpenDetails);
  }, []);

  // Show cached messages only for rooms already validated by the server
  useEffect(() => {
    if (!activeRawRoomIdGroupe) return;
    if (!validatedRoomsRef.current.has(activeRawRoomIdGroupe)) {
      setAllMessages([]);
      return;
    }
    getCachedMessages(activeRawRoomIdGroupe).then((cached) => {
      if (cached && cached.length > 0) {
        const messagesList = cached.filter(
          (msg) => !includesId(msg.deletedBy, user.userId)
        );
        setAllMessages(messagesList);
      }
    });
  }, [activeRawRoomIdGroupe, user.userId]);

  const renderContent = (message) => {
    if (message.nature === "texte" || message.nature === "code_meeting") {
      return message.contenu;
    } else if (message.nature === "fichier" || message.nature === "messageVocal") {
      return null;
    }
  };

  const getFormattedDate = (timeUnix) => formatMessageDate(timeUnix);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const containerTop = el.getBoundingClientRect().top;
    const separators = el.querySelectorAll("[data-date]");
    let currentDate = null;
    let bestTop = -Infinity;
    separators.forEach((sep) => {
      const top = sep.getBoundingClientRect().top;
      if (top < containerTop && top > bestTop) {
        bestTop = top;
        currentDate = sep.getAttribute("data-date");
      }
    });
    setStickyDate(currentDate);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, [allMessages, handleScroll]);

  // IntersectionObserver pour charger plus de messages
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingMore &&
          allMessages.length > 0 &&
          crew
        ) {
          setLoadingMore(true);
          const oldestMessage = allMessages[allMessages.length - 1];
          socket.emit("chargerPlusDeMessages", {
            roomId: activeRawRoomIdGroupe,
            beforeTimeUnix: oldestMessage.timeUnix,
            workspaceId,
          });
        }
      },
      { root: scrollRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, allMessages, crew, activeRawRoomIdGroupe, workspaceId]);

  return (
    <>
      {crew ? (
        <div
          className={`tyn-main tyn-chat-content aside-collapsed ${
            displayDiscussionForMiniScreen ? " main-shown " : ""
          }`}
          id="tynMain"
        >
          <div className="tyn-chat-head">
            <GroupeInfo
              show={show}
              setShow={setShow}
              enleverRecherche={displayDiscussionForMiniScreen}
              typingUsers={typingUsers}
            />
            <>
              <ul className="tyn-list-inline gap gap-3 ms-auto">
                <li className="d-sm-block">
                  <button
                    className="btn btn-icon btn-light js-toggle-chat-search"
                    onClick={() => {
                      setDisplayOffCanvasRight(true);
                    }}
                  >
                    {listOptionsDiscussion[0]}
                  </button>
                </li>
              </ul>
            </>
          </div>
          <div
            className="tyn-chat-body js-scroll-to-end"
            id="tynChatBody"
            style={{ position: "relative" }}
          >
            {stickyDate && (
              <div className="sticky-date-overlay">
                <span
                  className="date-of-message text-capitalize"
                  style={{ backgroundColor: bg, color }}
                >
                  {stickyDate}
                </span>
              </div>
            )}
            <div
              className="tyn-reply box-messages-container background-div-image"
              id="tynReply__groupe"
              ref={scrollRef}
            >
              <>
                {typingUsers.length > 0 &&
                  typingUsers.map((writer) => (
                    <div
                      key={writer.userId}
                      className="tyn-reply-item incoming"
                    >
                      <div className="tyn-reply-avatar">
                        <div className="tyn-media tyn-size-md tyn-circle">
                          <Person
                            person={{
                              image: writer.userImage || "",
                              nom: writer.nom || "",
                              prenom: writer.prenom || "",
                              colors: writer.color,
                              userId: writer.userId,
                            }}
                          />
                        </div>
                      </div>
                      <div className="tyn-reply-group">
                        <div className="tyn-reply-bubble">
                          <div className="typing-bubble tyn-reply-text">
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                            <span className="typing-dot"></span>
                          </div>
                          <div
                            className="typing-indicator-name"
                            style={{ color }}
                          >
                            {writer.prenom}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                {allMessages.map((message, idx) => {
                  const currentDate = getFormattedDate(message.timeUnix);
                  const nextMessageDate =
                    idx < allMessages.length - 1
                      ? getFormattedDate(allMessages[idx + 1].timeUnix)
                      : null;
                  const isFirstMessageOfDay = currentDate !== nextMessageDate;

                  return (
                    <React.Fragment key={message.messageId}>
                      {idsEqual(user.userId, message.emetteur) ? (
                        <MyMessage
                          contenu={renderContent(message)}
                          object={message}
                          idx={idx}
                          onOpenGallery={handleOpenGallery}
                          onDelete={handleDeleteMessage}
                          onSwipeReply={handleSwipeReply}
                        />
                      ) : (
                        <YourMessage
                          contenu={renderContent(message)}
                          object={message}
                          isGroupe={true}
                          groupe={crew}
                          onOpenGallery={handleOpenGallery}
                          onDelete={handleDeleteMessage}
                          onSwipeReply={handleSwipeReply}
                        />
                      )}
                      {isFirstMessageOfDay && (
                        <div
                          className="tyn-reply-separator ligne-qui-separe"
                          data-date={currentDate}
                        >
                          <span
                            className={`date-of-message text-capitalize${
                              currentDate === "Aujourd'hui"
                                ? " animate__heartBeat animate__infinite animate__animated"
                                : ""
                            }`}
                            style={{ backgroundColor: bg, color }}
                          >
                            {currentDate}
                          </span>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Sentinel pour le chargement de messages plus anciens */}
                <div ref={sentinelRef} style={{ height: 1 }} />
                {loadingMore && (
                  <div className="text-center py-2">
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                )}
              </>
            </div>
          </div>
          <ReplyPreview replyTo={replyTo} onClear={() => setReplyTo(null)} />
          <ChatForm
            conversation="groupe"
            isMuted={crew.mutedMembers?.some((id) => idsEqual(id, user.userId))}
            replyTo={replyTo}
            onClearReply={() => setReplyTo(null)}
          />
          <OffCanvasRightGroupe
            displayOffCanvasRight={displayOffCanvasRight}
            setDisplayOffCanvasRight={setDisplayOffCanvasRight}
            crew={crew}
            setCrew={setCrew}
            allMessages={allMessages}
          />
          <MediaGalleryModal
            media={allMedia}
            currentIndex={galleryIndex}
            isOpen={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            onPrev={() => setGalleryIndex((i) => Math.max(0, i - 1))}
            onNext={() =>
              setGalleryIndex((i) => Math.min(allMedia.length - 1, i + 1))
            }
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
}
